import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from '../../app/store';
import { apiEndPoints } from '../../common/api.endPoints';
import { HttpService } from '../../common/http.service';
import { attempt } from '../../common/utilities';

interface ErroredTask {
  createdAt: string;
  updatedAt: string;
  taskType: string;
  taskDetails: string | null;
  applicationId: number;
  taskId: number;
  product: string;
  carrier: string;
  carrierPolicyNumber: string;
  underwriter: string | null;
  casemanager: string | null;
  uwx: string | null;
  errorMessage?: string;
  evidenceId: number;
  retrying?: boolean;
  retried?: boolean;
}

export const erroredTaskListSlice = createSlice({
  name: 'erroredTaskList',
  initialState: {
    reload: true,
    loading: false,
    error: false,
    items: [] as ErroredTask[],
    pagination: {
      count: 0,
      page: 0,
      pageSize: 10,
    },
    filters: {
      taskType: null as string | null,
      product: null as string | null,
      carrier: null as string | null,
    },
  },
  reducers: {
    onPageUpdate(state, action: PayloadAction<{ page: number }>) {
      // If any item in the list is retried or being retried instead of changing
      // the page just refresh the current page.
      if (
        state.items.every(
          item => !Boolean(item.retrying) && !Boolean(item.retried),
        )
      ) {
        state.pagination.page = action.payload.page;
      }

      state.reload = true;
    },
    changeTaskTypeFilter(
      state,
      action: PayloadAction<{
        taskType: string;
      }>,
    ) {
      const newTaskType = action.payload.taskType || null;
      if (state.filters.taskType !== newTaskType) {
        state.filters.taskType = newTaskType;
        state.pagination.page = 0;
        state.reload = true;
      }
    },
    changeProductCarrierFilter(
      state,
      action: PayloadAction<{
        product: string;
        carrier: string;
      }>,
    ) {
      const product = action.payload.product || null;
      const carrier = action.payload.carrier || null;
      if (
        state.filters.product !== product ||
        state.filters.carrier !== carrier
      ) {
        state.filters.product = product;
        state.filters.carrier = carrier;
        state.pagination.page = 0;
        state.reload = true;
      }
    },
    onFetchErroredTasks(state) {
      state.reload = false;
      state.loading = true;
    },
    onFetchErroredTasksError(state) {
      state.loading = false;
      state.error = true;
    },
    onFetchErroredTasksSuccess(
      state,
      action: PayloadAction<{
        tasks: ErroredTask[];
        pagination: { count: number };
      }>,
    ) {
      state.loading = false;
      state.error = false;
      state.items = action.payload.tasks;
      state.pagination.count = action.payload.pagination.count;
    },
    onTaskRetry(
      state,
      action: PayloadAction<{
        taskId: number;
      }>,
    ) {
      const task = state.items.find(
        item => item.taskId === action.payload.taskId,
      );

      if (task) {
        task.retrying = true;
      }
    },
    onTaskRetrySuccess(
      state,
      action: PayloadAction<{
        taskId: number;
      }>,
    ) {
      const task = state.items.find(
        item => item.taskId === action.payload.taskId,
      );
      if (task) {
        task.retrying = false;
        task.retried = true;
      }
    },
    onTaskRetryError(
      state,
      action: PayloadAction<{
        taskId: number;
      }>,
    ) {
      const task = state.items.find(
        item => item.taskId === action.payload.taskId,
      );
      if (task) {
        task.retrying = false;
      }
    },
  },
});

export const fetchErroredTasks =
  (
    page: number,
    pageSize: number,
    filters: {
      taskType: string | null;
      product: string | null;
      carrier: string | null;
    },
  ): AppThunk =>
  async dispatch => {
    dispatch(erroredTaskListActions.onFetchErroredTasks());

    let url = `${apiEndPoints.erroredTasks}?page=${page}&pageSize=${pageSize}`;

    if (filters.taskType) {
      url += `&taskType=${filters.taskType}`;
    }
    if (filters.product) {
      url += `&product=${filters.product}`;
    }
    if (filters.carrier) {
      url += `&carrier=${filters.carrier}`;
    }

    const [response, err] = await attempt(HttpService.getInstance().get(url));
    if (!response || err) {
      dispatch(erroredTaskListActions.onFetchErroredTasksError());
      return;
    }

    const { tasks, pagination } = response.parsedBody;

    dispatch(
      erroredTaskListActions.onFetchErroredTasksSuccess({ tasks, pagination }),
    );
  };

export const erroredTaskListReducer = erroredTaskListSlice.reducer;
export const erroredTaskListActions = erroredTaskListSlice.actions;
